import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { TIPOLOGIE_LAVORAZIONE } from '@/lib/f1f2';
import { SIGLE_PROVINCE } from '@/lib/territorio';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

const emptyForm = {
  id: '', codiceSito: '', nomeSito: '',
  tipologiaLavorazione: '__none__',
  ditta: '', comune: '', provincia: '',
  importo: '',
};

export default function PracticeForm({ editPractice, onClose }) {
  const { userProfile } = useAuth();
  const { addPractice, updatePractice, ditte } = useData();

  const isEditing = !!editPractice;
  const [open, setOpen] = useState(isEditing);
  const [form, setForm] = useState(() => {
    if (!editPractice) return emptyForm;
    return {
      id: String(editPractice.id),
      codiceSito: editPractice.codiceSito || '',
      nomeSito: editPractice.nomeSito || '',
      tipologiaLavorazione: editPractice.tipologiaLavorazione || '__none__',
      ditta: editPractice.ditta || '',
      comune: editPractice.comune || '',
      provincia: editPractice.provincia || '',
      importo: editPractice.importo != null ? String(editPractice.importo) : '',
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const editingId = isEditing ? String(editPractice.id) : null;
  const noDitteAvailable = !ditte || ditte.length === 0;

  // Opzioni Provincia: sigle correnti + (solo in modifica) l'eventuale valore
  // legacy della pratica non piu' in elenco (es. sigle sarde soppresse), per non
  // farlo sparire dal dropdown.
  const provinciaOptions = form.provincia && !SIGLE_PROVINCE.includes(form.provincia)
    ? [form.provincia, ...SIGLE_PROVINCE]
    : SIGLE_PROVINCE;

  const handleOpenChange = (o) => {
    setOpen(o);
    if (!o) {
      setForm(emptyForm);
      setError(null);
      setSuccess(false);
      onClose?.();
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(false);
    if (!form.id.trim()) { setError('ID Lavorazione obbligatorio'); return; }
    if (!form.codiceSito.trim()) { setError('Codice Sito obbligatorio'); return; }
    if (!form.nomeSito.trim()) { setError('Nome Sito obbligatorio'); return; }
    if (!form.ditta.trim()) { setError('Ditta obbligatoria'); return; }
    if (!form.comune.trim()) { setError('Comune obbligatorio'); return; }
    if (!form.provincia.trim()) { setError('Provincia obbligatoria'); return; }
    if (!form.tipologiaLavorazione || form.tipologiaLavorazione === '__none__') { setError('Tipo di Intervento obbligatorio'); return; }
    if (form.importo === '' || form.importo == null) { setError('Importo obbligatorio'); return; }

    const data = {
      id: form.id.trim(),
      codiceSito: form.codiceSito.trim(),
      nomeSito: form.nomeSito.trim(),
      tipologiaLavorazione: form.tipologiaLavorazione,
      ditta: form.ditta.trim(),
      studioProgettazione: userProfile?.studioProgettazione || null,
      comune: form.comune.trim(),
      provincia: form.provincia.trim().toUpperCase(),
      importo: parseFloat(form.importo),
    };

    setSaving(true);
    try {
      if (editingId) {
        await updatePractice(editingId, data);
      } else {
        await addPractice(data);
      }
      setSuccess(true);
      setForm(emptyForm);
      setTimeout(() => { handleOpenChange(false); }, 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const dialogContent = (
    <DialogContent className="sm:max-w-6xl">
      <DialogHeader>
        <DialogTitle>{editingId ? 'Modifica Attività' : 'Nuova Attività'}</DialogTitle>
        <DialogDescription>{editingId ? `Stai modificando l'attività ${editingId}.` : 'Inserisci i dati della nuova attività.'}</DialogDescription>
      </DialogHeader>
      <div className="overflow-visible -mx-6 px-6">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col style={{ width: '7%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '21%' }} />
          </colgroup>
          <thead>
            <tr className="bg-fw-light border-b border-fw-border">
              {['ID Lav. *', 'Cod. Sito *', 'Nome Sito *', 'Tipo Intervento *', 'Ditta *', 'Comune *', 'Prov. *', 'Importo Tot. Preventivo (€) *'].map((h) => (
                <th key={h} className="text-xs font-semibold uppercase tracking-wider text-fw-muted px-2 py-2 text-left leading-tight">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-2 py-2">
                <input type="text" value={form.id} onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))} className="w-full border border-fw-border rounded px-2 py-1.5 text-sm font-mono focus:ring-2 focus:ring-fw-yellow focus:border-fw-yellow outline-none" placeholder="es. 12345" />
              </td>
              <td className="px-2 py-2">
                <input type="text" value={form.codiceSito} onChange={(e) => setForm((f) => ({ ...f, codiceSito: e.target.value }))} className="w-full border border-fw-border rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-fw-yellow focus:border-fw-yellow outline-none" placeholder="es. MI-001" />
              </td>
              <td className="px-2 py-2">
                <input type="text" value={form.nomeSito} onChange={(e) => setForm((f) => ({ ...f, nomeSito: e.target.value }))} className="w-full border border-fw-border rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-fw-yellow focus:border-fw-yellow outline-none" placeholder="es. Milano Centro" />
              </td>
              <td className="px-2 py-2">
                <Select value={form.tipologiaLavorazione} onValueChange={(v) => setForm((f) => ({ ...f, tipologiaLavorazione: v }))}>
                  <SelectTrigger className="w-full text-sm"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {TIPOLOGIE_LAVORAZIONE.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </td>
              <td className="px-2 py-2">
                <Select value={form.ditta || '__none__'} onValueChange={(v) => setForm((f) => ({ ...f, ditta: v === '__none__' ? '' : v }))}>
                  <SelectTrigger className="w-full text-sm">
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {ditte.map((d) => (
                      <SelectItem key={d._docId} value={d.nome}>{d.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </td>
              <td className="px-2 py-2">
                <input type="text" value={form.comune} onChange={(e) => setForm((f) => ({ ...f, comune: e.target.value }))} className="w-full border border-fw-border rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-fw-yellow focus:border-fw-yellow outline-none" placeholder="es. Milano" />
              </td>
              <td className="px-2 py-2">
                <Select value={form.provincia || '__none__'} onValueChange={(v) => setForm((f) => ({ ...f, provincia: v === '__none__' ? '' : v }))}>
                  <SelectTrigger className="w-full text-sm min-w-[60px]" data-testid="provincia-select">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="__none__">—</SelectItem>
                    {provinciaOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </td>
              <td className="px-2 py-2">
                <input type="number" step="0.01" min="0" value={form.importo} onChange={(e) => setForm((f) => ({ ...f, importo: e.target.value }))} className="w-full border border-fw-border rounded px-2 py-1.5 text-sm font-mono text-right focus:ring-2 focus:ring-fw-yellow focus:border-fw-yellow outline-none" placeholder="0.00" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {userProfile?.studioProgettazione && (
        <div className="text-sm text-fw-muted bg-fw-light rounded-md px-3 py-2">
          Studio Progettazione: <strong>{userProfile.studioProgettazione}</strong> (assegnato automaticamente)
        </div>
      )}

      {noDitteAvailable && (
        <div className="flex items-start gap-2 text-sm text-fw-muted bg-yellow-50 border border-fw-yellow/30 rounded-md px-3 py-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-fw-yellow-hover" />
          <span>Nessuna Ditta disponibile nel dropdown. Chiedi a HQ o Fastweb di invitare almeno una Ditta dalla pagina Inviti per popolare l'elenco.</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-fw-red bg-red-50 border border-fw-red/20 rounded-md px-3 py-2">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-fw-green/20 rounded-md px-3 py-2">
          <CheckCircle2 className="w-4 h-4 shrink-0" /> {editingId ? 'Attività aggiornata con successo' : 'Attività aggiunta con successo'}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>Annulla</Button>
        <Button className="bg-fw-yellow hover:bg-fw-yellow-hover text-fw-dark" onClick={handleSubmit} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : editingId ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {saving ? 'Salvataggio...' : editingId ? 'Aggiorna Attività' : 'Salva Attività'}
        </Button>
      </div>
    </DialogContent>
  );

  if (isEditing) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        {dialogContent}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-fw-yellow hover:bg-fw-yellow-hover text-fw-dark" data-testid="open-add-modal" onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Aggiungi Attività
        </Button>
      </DialogTrigger>
      {dialogContent}
    </Dialog>
  );
}
